import Core           from '@/core/Core';
import Comment        from '@/core/services/Comments/Comment';
import CommentsDAO    from '@/core/services/Comments/CommentsDAO';
import EntityPointer  from '@/domains/EntityPointer/EntityPointer';
import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";

/**
 * Сервис для работы с комментариями
 */
class CommentsService {

	// ============================================================
	// CRUD
	// ============================================================

	/**
	 * Загрузить список комментариев для сущности.
	 * Доступен только админам — на бэке стоит проверка isAdmin.
	 */
	static async getList(
    params: Parameters<typeof CommentsDAO.getList>[0],
    showLoader: boolean = false,
  ): Promise<Comment[]> {
		const response = await CommentsDAO.getList(params, showLoader);

		return Core.utils.hydrateArray(response.data, Comment.fromServer);
	}

  static async getListPaginated(
    params: Parameters<typeof CommentsDAO.getList>[0] & {
      pagination?: Partial<RpcDataPagination>;
      sort?: Partial<RpcDataSort>;
    },
    showLoader: boolean = false,
    onError?: (error: Error) => void,
  ): Promise<Comment[]> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await CommentsDAO.getList(filters, showLoader, pagination, sort, onError);

    return Core.utils.hydrateArray(response.data, Comment.fromServer);
  }

	/**
	 * Создать комментарий.
	 * Возвращает сохранённый коммент с проставленными id и createdAt — или null при ошибке.
	 */
	static async add(comment: Comment): Promise<Comment | null> {
		if (!Core.utils.string.isNotEmptyString(comment.body)) {
			return null;
		}

		const response = await CommentsDAO.add(comment);

		if (response.ok === false) {
			Core.showNotification.error('Ошибка при добавлении комментария');
			return null;
		}

		return Core.utils.hydrate(response.data, Comment.fromServer);
	}

	/**
	 * Обновить комментарий
	 */
	static async update(comment: Comment): Promise<boolean> {
		if (!Core.utils.string.isNotEmptyString(comment.body)) {
			return false;
		}

		const response = await CommentsDAO.update(comment);

		if (response.ok === false) {
			Core.showNotification.error('Ошибка при обновлении комментария');
			return false;
		}

		return true;
	}

	/**
	 * Удалить комментарий
	 */
	static async delete(id: number): Promise<boolean> {
		const response = await CommentsDAO.delete(id);

		if (response.ok === false) {
			Core.showNotification.error('Ошибка при удалении комментария');
			return false;
		}

		return true;
	}

	// ============================================================
	// Прочтение
	// ============================================================

	/**
	 * Пометить один коммент прочитанным для текущего пользователя
	 */
	static async markAsRead(comment: Comment): Promise<boolean> {
		const response = await CommentsDAO.markAsRead(comment);

		return response.ok;
	}

	/**
	 * Пометить все комменты сущности прочитанными для текущего пользователя
	 */
	static async markAllAsRead(parentEntity: EntityPointer): Promise<boolean> {
		const response = await CommentsDAO.markAllAsRead(parentEntity);

		return response.ok;
	}
}

export default CommentsService;
